/**
 * <facet-filters-form>
 *
 * Progressive enhancement for the collection filter/sort form
 * (sections/main-collection.liquid + snippets/facets.liquid).
 *
 * Without this script the form is a plain `method="get"` form: any input
 * change requires the (JS-hidden, noscript-only) submit button and a full
 * page reload, and Shopify's native filter engine renders the page from the
 * query string as usual.
 *
 * With this script: any change to a filter input or the sort <select>
 * serializes the form, fetches `?section_id=<id>&<querystring>` (the
 * Section Rendering API), and swaps #ProductGrid, #FacetsWrapper and
 * #ProductCount with the freshly rendered markup, then pushes the new URL.
 * Pagination links and active-filter "remove" pills are plain <a> tags
 * pointing at the collection URL, so clicks on them are intercepted the
 * same way instead of causing a full navigation.
 */
if (!customElements.get('facet-filters-form')) {
  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.sectionId = this.dataset.sectionId;
      this.debounceTimer = null;

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onDesktopChange = this.onDesktopChange.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);

      // The <details> drawer is server-rendered `open` so it works with no
      // JS at all. Once JS is available, collapse it by default on mobile
      // (real "Filters" button UX) and force it open on desktop (CSS alone
      // can't override a closed <details>'s native content-visibility).
      this.desktopMq = window.matchMedia('(min-width: 990px)');
      this.desktopMq.addEventListener('change', this.onDesktopChange);
      this.syncDrawerForViewport(this.desktopMq.matches);

      this.onDrawerToggle = () => {
        const drawer = this.querySelector('#FacetsWrapper');
        if (!drawer) return;
        // A toggle that leaves the drawer in exactly the state this viewport
        // would put it in is not the visitor overriding anything. It is the
        // pre-paint collapse in sections/main-collection.liquid, whose toggle
        // event is queued during parsing and so arrives here a moment after
        // this listener exists. Treating that as a preference would pin the
        // drawer open for the rest of the session. Only a state that diverges
        // from the viewport default is a real choice.
        if (drawer.open === this.desktopMq.matches) return;
        drawer.dataset.userToggled = 'true';
      };
      this.addEventListener('toggle', this.onDrawerToggle, true);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.desktopMq) this.desktopMq.removeEventListener('change', this.onDesktopChange);
    }

    onDesktopChange(event) {
      this.syncDrawerForViewport(event.matches);
    }

    syncDrawerForViewport(isDesktop) {
      const drawer = this.querySelector('#FacetsWrapper');
      if (!drawer) return;
      if (isDesktop) {
        drawer.open = true;
      } else if (!drawer.dataset.userToggled) {
        drawer.open = false;
      }
    }

    isDebounced(input) {
      return input.type === 'range' || input.type === 'number';
    }

    onInput(event) {
      const input = event.target;
      if (!this.isDebounced(input)) return;
      this.syncPriceUI(input);
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchAndRender(), 300);
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('input, select')) return;
      if (this.isDebounced(input)) return; // handled (debounced) by onInput
      this.fetchAndRender();
    }

    onSubmit(event) {
      event.preventDefault();
      clearTimeout(this.debounceTimer);
      this.fetchAndRender();
    }

    onClick(event) {
      const link = event.target.closest('[data-facet-remove], .facets__pill-link, .pagination__link');
      if (!link || link.tagName !== 'A' || !link.href) return;
      event.preventDefault();
      const url = new URL(link.href);
      /* Only a PAGE change scrolls back to the top. Removing a filter pill
         should leave the reader where they are - the results update in place
         and yanking them upward loses their position for no reason. Turning a
         page is the opposite: the content is entirely new, and staying at the
         bottom leaves them looking at a footer wondering what happened. */
      this.fetchAndRender(url.search, link.classList.contains('pagination__link'));
    }

    // Keep the two range thumbs / number fields and the visual fill in sync
    // when either a range or a number input changes.
    syncPriceUI(changedInput) {
      const slider = changedInput.closest('.facets__price-slider') || this.querySelector('[data-price-slider]');
      if (!slider) return;

      const minRange = slider.querySelector('[data-price-range-min]');
      const maxRange = slider.querySelector('[data-price-range-max]');
      const minNumber = this.querySelector('[data-price-number-min]');
      const maxNumber = this.querySelector('[data-price-number-max]');
      if (!minRange || !maxRange) return;

      if (changedInput === minNumber) minRange.value = changedInput.value || 0;
      if (changedInput === maxNumber) maxRange.value = changedInput.value || slider.dataset.rangeMax;
      if (changedInput === minRange && minNumber) minNumber.value = changedInput.value;
      if (changedInput === maxRange && maxNumber) maxNumber.value = changedInput.value;

      // Prevent the min thumb from crossing the max thumb and vice versa.
      if (Number(minRange.value) > Number(maxRange.value)) {
        if (changedInput === minRange || changedInput === minNumber) {
          minRange.value = maxRange.value;
          if (minNumber) minNumber.value = maxRange.value;
        } else {
          maxRange.value = minRange.value;
          if (maxNumber) maxNumber.value = minRange.value;
        }
      }

      const rangeMax = Number(slider.dataset.rangeMax) || 1;
      const minPercent = (Number(minRange.value) / rangeMax) * 100;
      const maxPercent = (Number(maxRange.value) / rangeMax) * 100;
      slider.style.setProperty('--min-percent', `${minPercent}%`);
      slider.style.setProperty('--max-percent', `${maxPercent}%`);
    }

    buildQueryString() {
      const formData = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (value === '' || value === null) continue;
        params.append(key, value);
      }
      return params.toString();
    }

    fetchAndRender(explicitQueryString, scrollToTop) {
      const queryString = explicitQueryString !== undefined ? explicitQueryString.replace(/^\?/, '') : this.buildQueryString();
      const pathname = window.location.pathname;
      const fetchUrl = `${pathname}?section_id=${this.sectionId}&${queryString}`;
      const pushUrl = queryString ? `${pathname}?${queryString}` : pathname;

      this.classList.add('facets-loading');

      fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          this.renderFragment(html);
          window.history.pushState({ facets: true }, '', pushUrl);
          if (scrollToTop) this.scrollToResults();
        })
        .catch((error) => {
          console.error('[facets] fetch failed, falling back to full navigation', error);
          window.location.href = pushUrl;
        })
        .finally(() => {
          this.classList.remove('facets-loading');
        });
    }

    /* Back to the top of the results after turning a page.
     *
     * A pagination link sits at the BOTTOM of the grid, so without this the
     * reader is left staring at the footer of a page whose contents changed
     * entirely above them - and since a shorter page also moves the pagination
     * bar out from under the cursor, it reads as "the click did nothing".
     *
     * To the grid rather than the document top: the collection heading and
     * filters are above it and re-reading them is not why anyone turned a
     * page. Instant rather than smooth, because a long smooth scroll after a
     * click that already felt slow just extends the wait. */
    scrollToResults() {
      const anchor = document.getElementById('ProductGrid') || this;
      const top = anchor.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'instant' });
    }

    renderFragment(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const ids = ['ProductGrid', 'FacetsWrapper', 'ProductCount', 'ProductCountToolbar', 'Pagination'];

      // First pass: read each target's position out of `doc` *before* any
      // DOM surgery happens. replaceWith()/appendChild() transfer a node
      // between documents, detaching it from `doc`'s tree — so reading
      // sibling ids lazily inside a single mutating pass would see an
      // already-corrupted tree by the time a later id (e.g. Pagination,
      // whose sibling is the earlier-processed ProductGrid) is handled.
      const targets = ids.map((id) => {
        const newEl = doc.getElementById(id);
        if (!newEl) return { id, newEl: null };
        const parent = newEl.parentElement;
        const siblingIds = parent ? Array.from(parent.children).map((el) => el.id || null) : [];
        return { id, newEl, siblingIds, index: siblingIds.indexOf(id), parentId: parent ? parent.id : null };
      });

      targets.forEach(({ id, newEl, siblingIds, index, parentId }) => {
        const currentEl = document.getElementById(id);

        if (!newEl) {
          // e.g. Pagination disappears when a filtered result fits on one page
          if (currentEl) currentEl.remove();
          return;
        }

        // The server always renders the drawer `open` (no-JS safe default);
        // once JS has taken over, keep whatever open/closed state the user
        // (or syncDrawerForViewport) currently has instead of resetting it.
        if (id === 'FacetsWrapper' && currentEl && currentEl.tagName === 'DETAILS') {
          newEl.open = currentEl.open;
          if (currentEl.dataset.userToggled) newEl.dataset.userToggled = currentEl.dataset.userToggled;
        }

        if (currentEl) {
          currentEl.replaceWith(newEl);
        } else {
          // e.g. Pagination reappearing after being removed by an earlier
          // swap (result now spans more than one page again) — there's no
          // live element to replace, so find where it belongs using the
          // sibling ids captured above (ProductGrid, its neighbor, is
          // processed first so it's already live by the time we get here).
          this.insertMissingElement(newEl, siblingIds, index, parentId);
        }
      });

      this.form = this.querySelector('form');

      /* Every product card carries [data-reveal], which base.css hides at
         opacity 0 until assets/reveal.js adds .is-revealed. A replaceWith()
         fires no event, so nothing was processing the new grid - page two of
         any collection rendered COMPLETELY BLANK while sitting fully present
         in the DOM, and the merchant read that as "the click did nothing" and
         clicked again.

         Guarded because reveal.js is loaded with defer and a fast filter
         click can land before it has parsed. The initial page is already
         revealed by then, so skipping one pass costs nothing. */
      if (window.ThemeReveal) window.ThemeReveal.init(document);

      /* #ProductGrid and #Pagination were just replaced wholesale. Anything
         holding a reference into them - infinite-scroll.js watches the
         pagination to know what "next" is - is now pointing at detached
         nodes and has to rebuild from what is on screen. */
      document.dispatchEvent(new CustomEvent('facets:updated'));
    }

    // Inserts `newEl` (present in the fetched fragment but absent from the
    // live DOM) next to the nearest of its fetched-fragment siblings that
    // does exist live, walking outward from its original position.
    insertMissingElement(newEl, siblingIds, index, parentId) {
      for (let offset = 1; offset < siblingIds.length; offset += 1) {
        const beforeId = siblingIds[index - offset];
        if (beforeId) {
          const anchor = document.getElementById(beforeId);
          if (anchor) {
            anchor.after(newEl);
            return;
          }
        }

        const afterId = siblingIds[index + offset];
        if (afterId) {
          const anchor = document.getElementById(afterId);
          if (anchor) {
            anchor.before(newEl);
            return;
          }
        }
      }

      const liveParent = parentId && document.getElementById(parentId);
      if (liveParent) {
        liveParent.appendChild(newEl);
        return;
      }

      console.error('[facets] could not find an insertion point for', newEl.id);
    }

    onPopState(event) {
      const search = window.location.search;
      this.fetchAndRenderNoPush(search);
    }

    fetchAndRenderNoPush(queryString) {
      const pathname = window.location.pathname;
      const qs = queryString.replace(/^\?/, '');
      const fetchUrl = `${pathname}?section_id=${this.sectionId}&${qs}`;

      fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => this.renderFragment(html))
        .catch((error) => console.error('[facets] popstate fetch failed', error));
    }
  }

  customElements.define('facet-filters-form', FacetFiltersForm);
}
